import { DateTime, Zone } from 'luxon';

type Args = {
  /** Date string in ISO format */
  value?: string;
  /** If not specified, then UTC will be used */
  timezone?: string | Zone;
  /** Predefined formats that should be used within the app.
   * If you need to use custom format, then pass it as 'customFormat' prop to override predefined formats */
  mode?: 'date' | 'time' | 'dateTime';
  /** Custom format followed Intl.DateTimeFormatOptions model. Note - it will override 'mode' prop. */
  customFormat?: Intl.DateTimeFormatOptions;
  /** If not specified, then 'en-GB' will be used */
  locale?: string;
};

const getPredefinedFormat = (mode: Args['mode']) => {
  switch (mode) {
    case 'date':
      return DateTime.DATE_SHORT;
    case 'time':
      return DateTime.TIME_24_SIMPLE;
    case 'dateTime':
      return DateTime.DATETIME_SHORT;
    default:
      return DateTime.DATE_SHORT;
  }
};

export const formatDateTime = ({ value, timezone = 'utc', mode = 'date', customFormat, locale = 'en-GB' }: Args) => {
  if (value) {
    const date = DateTime.fromISO(value).setZone(timezone);
    const format = customFormat || getPredefinedFormat(mode);

    return date.isValid ? date.toLocaleString({ ...format }, { locale }) : '—';
  }

  return '—';
};

const convertDateToMillis = (date: string | number) => (typeof date === 'number' ? date : DateTime.fromISO(date.split('T')[0]).toMillis());

/** Helper to use mainly with <DatePicker /> component.
 * It may looks quite strange, but <DatePicker /> from shared lib is very bugy and I need to rewrite it.
 * Then I'll tune below helper function as well.  */
export const isDateWithinRange = (date?: string | number, start?: string | number, end?: string | number) => {
  if (!date) return false;

  /* When start and end dates are not provided, then safety expect that date is within the range. */
  if (!start) return true;

  /* Remove time part from dateTime and operate on dates only */
  const dateMs = convertDateToMillis(date);
  const startMs = convertDateToMillis(start);
  const endMs = convertDateToMillis(end ?? '');

  return endMs ? dateMs >= startMs && dateMs <= endMs : dateMs === startMs;
};

export const getUtcDateFromLocal = (date?: number) => {
  if (date) {
    const localDate = new Date(date);
    return Date.UTC(localDate?.getUTCFullYear(), localDate.getUTCMonth(), localDate.getDate());
  }
};
